import React from 'react';
import AgoraRTC, {
    IAgoraRTCClient,
    IAgoraRTCRemoteUser,
    ICameraVideoTrack,
    ILocalVideoTrack,
    IMicrophoneAudioTrack,
    IRemoteVideoTrack,
    UID,
    VideoEncoderConfigurationPreset
} from 'agora-rtc-sdk-ng';
import styles from './styles.module.scss';
import {connect} from 'react-redux';
import {of, Subscription} from 'rxjs';
import {catchError, tap} from 'rxjs/operators';
// import UnloadHandler from "../UnloadHandler";
import {RootState} from '../../../store/reducers';
import VideoSettings from './VideoSettings';
import {changeScreenMode} from '../../../store/reducers/videoCallDetailsSlice';
import {changeOnlineConsultationStatus} from '../../../store/reducers/onlineConsultationSlice';
import {ConsultationStatus, IAgoraVideoCallProps, IAgoraVideoCallState, UserRole} from '../../../types';
import {confirmPresenceAPI, isNotNullOrUndefined, isSameValue, setConsultationDurationAPI} from '../../..';
import {
    audioInputDeviceSelector,
    audioOutputDeviceSelector,
    videoInputDeviceSelector
} from '../../../store/selectors/videoCallDetailsSelectors';
import Counter from '../Counter';


type Props = IAgoraVideoCallProps;
type State = IAgoraVideoCallState;

class AgoraVideoCall extends React.Component<Props, State> {
    private subscriptions: Subscription[] = [];
    private participantPresenceInterval: any = null;
    private client: IAgoraRTCClient | null = null;
    private localAudioTrack: IMicrophoneAudioTrack | null = null;
    private localVideoTrack: ICameraVideoTrack | null = null;

    constructor(props: Props) {
        super(props);

        this.state = {
            displayMode: 'pip',
            streamList: [],
            readyState: false,
            isFullScreen: false, // this.props.isFullScreenMode,
            isConsultationFinished: false,
            isMicEnabled: true,
            isCameraEnabled: true,
            windowWidth: window.innerWidth,
            localStreamId: null,
            isFullScreenMode: false,
            isSettingsModalShown: false,
        }
    }

    componentDidMount() {
        if (this.props.attendeeRole === UserRole.CANDIDATE && isNotNullOrUndefined(this.props.token) && isNotNullOrUndefined(this.props.uid)) {
            this.joinChannel();
        }

        window.addEventListener('resize', this.getDimensions);
    }

    componentDidUpdate(prevProps: Props) {
        if (this.props.videoInputDevice !== prevProps.videoInputDevice && this.props.videoInputDevice && this.localVideoTrack) {
            this.localVideoTrack.setDevice(this.props.videoInputDevice).then(
                () => console.log('videoInputDevice has changed.'),
                (err: any) => console.log('videoInputDevice change failed.', err)
            );
        }

        if (this.props.audioInputDevice !== prevProps.audioInputDevice && this.props.audioInputDevice && this.localAudioTrack) {
            this.localAudioTrack.setDevice(this.props.audioInputDevice).then(
                () => console.log('audioInputDevice has changed.'),
                (err: any) => console.log('audioInputDevice change failed.', err)
            );
        }

        if ((!isSameValue(this.props.token, prevProps.token) && this.props.token) ||
            (!isSameValue(this.props.uid, prevProps.uid) && this.props.uid)) {
            if (this.props.attendeeRole === UserRole.CANDIDATE) {
                this.joinChannel();
            }
        }
        // if (this.props.isFullScreenMode !== prevProps.isFullScreenMode) {
        //     this.setState({isFullScreen: this.props.isFullScreenMode});
        // }
    }

    componentWillUnmount() {
        this.setParticipantPresence(false);
        this.leaveChannel();

        this.subscriptions.forEach(subscription => subscription.unsubscribe());

        if (this.participantPresenceInterval) clearInterval(this.participantPresenceInterval);
        window.removeEventListener('resize', this.getDimensions);
    }

    private joinChannel = async () => {
        if (!this.props.channel) {
            return;
        }
        if (this.client) {
            this.leaveChannel();
        }

        const client = AgoraRTC.createClient({mode: 'live', codec: 'h264'});
        this.client = client;
        client.on('user-published', this.handleUserPublished);
        client.on('user-left', this.handleUserLeft);

        try {
            await client.setClientRole('host');
            const uid = await client.join(this.props.appId, this.props.channel, this.props.token, this.props.uid);
            console.log('User ' + uid + ' join channel successfully');
            if (this.client !== client) {
                return;
            }
            this.setState({localStreamId: 'ag-item-' + uid});

            const {audioTrack, videoTrack} = await this.createLocalTracks();
            if (this.client !== client) {
                audioTrack && audioTrack.close();
                videoTrack && videoTrack.close();
                return;
            }
            this.localAudioTrack = audioTrack;
            this.localVideoTrack = videoTrack;
            this.playVideoTrack(uid, videoTrack, true);

            const tracks = [audioTrack, videoTrack].filter(isNotNullOrUndefined) as (IMicrophoneAudioTrack | ICameraVideoTrack)[];
            if (tracks.length) {
                // In a live interactive streaming channel, whoever publishes is the host.
                await client.publish(tracks);
                this.setState({readyState: true});
            }
        } catch (err) {
            console.log('Joining video call failed', err);
            this.setState({readyState: false});
        }
    };

    // Tracks are created separately so a missing camera or microphone does not block the other one.
    private createLocalTracks = async () => {
        let audioTrack: IMicrophoneAudioTrack | null = null,
            videoTrack: ICameraVideoTrack | null = null;
        if (this.props.attendeeMode === 'audience') {
            return {audioTrack, videoTrack};
        }

        try {
            audioTrack = await AgoraRTC.createMicrophoneAudioTrack(
                this.props.audioInputDevice ? {microphoneId: this.props.audioInputDevice} : undefined
            );
        } catch (err) {
            console.log('getUserMedia (microphone) failed', err);
        }

        if (this.props.attendeeMode === 'audio-only') {
            return {audioTrack, videoTrack};
        }

        try {
            videoTrack = await AgoraRTC.createCameraVideoTrack({
                cameraId: this.props.videoInputDevice || undefined,
                encoderConfig: this.props.videoProfile as VideoEncoderConfigurationPreset
            });
        } catch (err) {
            console.log('getUserMedia (camera) failed', err);
        }

        return {audioTrack, videoTrack};
    };

    private handleUserPublished = async (user: IAgoraRTCRemoteUser, mediaType: 'audio' | 'video' | 'datachannel') => {
        if (!this.client || mediaType === 'datachannel') {
            return;
        }

        try {
            await this.client.subscribe(user, mediaType);
            console.log('Subscribe remote ' + mediaType + ' successfully: ' + user.uid);
        } catch (err) {
            console.log('Subscribe stream failed', err);
            return;
        }

        if (mediaType === 'video') {
            this.addStream(user.uid);
            this.playVideoTrack(user.uid, user.videoTrack, false);
        } else if (user.audioTrack) {
            user.audioTrack.play();
        }
    };

    private handleUserLeft = (user: IAgoraRTCRemoteUser) => {
        console.log('Peer has left: ' + user.uid);
        this.removeStream(user.uid);
    };

    private playVideoTrack = (uid: UID, track: ILocalVideoTrack | IRemoteVideoTrack | null | undefined, isLocal: boolean) => {
        const canvas = document.querySelector('#ag-canvas'),
            id = 'ag-item-' + uid;
        if (!canvas || !track) {
            return;
        }

        let dom = document.getElementById(id);
        if (!dom) {
            dom = document.createElement('section');
            dom.setAttribute('id', id);
            dom.setAttribute('class', `${styles.agItem}`);
            canvas.appendChild(dom);
        }
        dom.setAttribute('style', isLocal ?
            'z-index:9;position:absolute;top:1rem;left:1.5rem;height:15rem;width:15rem' :
            'z-index:1;width:100%;height:100%');
        track.play(dom);
    };

    removeStream = (uid: UID) => {
        const element = document.querySelector('#ag-item-' + uid);
        if (element && element.parentNode) {
            element.parentNode.removeChild(element)
        }
        this.setState(state => ({streamList: state.streamList.filter(item => item !== uid)}));
    };

    addStream = (uid: UID) => {
        this.setState(state => state.streamList.includes(uid) ? null : {streamList: state.streamList.concat([uid])});
    };

    handleCamera = (e: any) => {
        if (this.localVideoTrack) {
            e.currentTarget.classList.toggle('off');
            this.localVideoTrack.setMuted(this.state.isCameraEnabled);
            this.setState({isCameraEnabled: !this.state.isCameraEnabled});
        }
    };

    handleMic = (e: any) => {
        if (this.localAudioTrack) {
            e.currentTarget.classList.toggle('off');
            this.localAudioTrack.setMuted(this.state.isMicEnabled);
            this.setState({isMicEnabled: !this.state.isMicEnabled});
        }
    };

    // Does not touch component state, so it is safe to call from componentWillUnmount.
    private leaveChannel = () => {
        const client = this.client,
            localStreamId = this.state.localStreamId;

        this.client = null;
        this.localAudioTrack && this.localAudioTrack.close();
        this.localVideoTrack && this.localVideoTrack.close();
        this.localAudioTrack = null;
        this.localVideoTrack = null;

        this.state.streamList.forEach((uid: UID) => {
            const element = document.querySelector('#ag-item-' + uid);
            element && element.parentNode && element.parentNode.removeChild(element);
        });
        const localElement = localStreamId ? document.getElementById(localStreamId) : null;
        localElement && localElement.parentNode && localElement.parentNode.removeChild(localElement);

        if (client) {
            client.removeAllListeners();
            client.leave().then(
                () => console.log('Client succeed to leave.'),
                (err: any) => console.log('Client failed to leave.', err)
            );
        }
    };

    handleExit = (e?: any) => {
        if (e && e.currentTarget.classList.contains('disabled')) {
            return;
        }

        try {
            this.leaveChannel();
        } finally {
            this.setState({readyState: false, localStreamId: null, streamList: []});
            window.location.hash = ''
        }

        if (this.props.attendeeRole === UserRole.ORGANIZATION || this.props.attendeeRole === UserRole.HEADHUNTER) {
            let consultationDuration = Math.floor((new Date().getTime() - new Date(this.props.consultationStartTime).getTime()) / 1000) * 2;
            this.setState({isConsultationFinished: true});
            this.setParticipantPresence(false);
            if (this.participantPresenceInterval) {
                clearInterval(this.participantPresenceInterval);
            }
            this.changeConsultationStatus(consultationDuration);
        }
    };

    render() {
        const isCallInitiated = this.state.localStreamId !== null;

        return (
            <React.Fragment>
                <Counter onlineConsultation={this.props.consultation}
                         endConsultation={this.handleExit}
                         userSecret={this.props.userSecret}
                         userRole={this.props.attendeeRole}/>

                <div id="ag-canvas" className={styles.agCanvas}>
                    <button onClick={() => this.changeFullScreenMode()}
                            className={`${styles.fullScreenBtn} 
                            // ${!this.state.isMicEnabled ? styles.disabled : ''}
                            `}
                            title="Full screen">
                    </button>

                    <div className={`${styles.agBtnGroup} ag-btn-group`}>
                        {this.renderVideoControlBtn()}
                        {this.renderAudioControlBtn()}
                        {this.renderSettingsBtn()}
                        {/*{this.renderScreenSharingBtn()}*/}
                        {isCallInitiated ? this.renderExitBtn() : (!this.state.isConsultationFinished ? this.renderStartVideoCall() : null)}
                    </div>
                </div>

                {/*{this.props.attendeeRole === UserRole.ORGANIZATION ? <UnloadHandler /> : null}*/}
                {this.state.isSettingsModalShown ? (<VideoSettings isModalVisible={this.state.isSettingsModalShown}
                                                                   toggleModal={this.toggleSettingsModal}/>) : null}
            </React.Fragment>
        )
    }

    private renderStartVideoCall = () => {
        return (
            <React.Fragment>
                {(this.props.attendeeRole === UserRole.ORGANIZATION || this.props.attendeeRole === UserRole.HEADHUNTER)?
                    (<div className={styles.agBtnContainer}>
                        <button className={`${styles.agBtn} ${styles.startBtn}`}
                                onClick={() => {
                                    this.joinChannel();
                                    this.setParticipantPresence(true);
                                    this.props.changeOnlineConsultationStatus(ConsultationStatus.STARTED);
                                    if (this.participantPresenceInterval) clearInterval(this.participantPresenceInterval);
                                    this.participantPresenceInterval = setInterval(() => {
                                        this.setParticipantPresence(true);
                                    }, 50 * 1000);
                                }}>
                            <span className={styles.startBtnText}>Start Consultation</span>
                        </button>
                    </div>) :
                    null
                }
            </React.Fragment>
        )
    };
    private renderVideoControlBtn = () => {
        return (
            <React.Fragment>
                {this.props.attendeeMode === 'video' ?
                    (<div className={styles.agBtnContainer}>
                        <button onClick={this.handleCamera}
                                className={`${styles.agBtn} ${styles.videoControlBtn} ${!this.state.isCameraEnabled ? styles.disabled : ''}`}
                                title="Enable/Disable Video">
                        </button>
                    </div>) :
                    null
                }
            </React.Fragment>
        )
    };

    private renderAudioControlBtn = () => {
        return (
            <React.Fragment>
                {this.props.attendeeMode !== 'audience' ?
                    (<div className={styles.agBtnContainer}>
                        <button onClick={this.handleMic}
                                className={`${styles.agBtn} ${styles.audioControlBtn} ${!this.state.isMicEnabled ? styles.disabled : ''}`}
                                title="Enable/Disable Audio">
                        </button>
                    </div>) :
                    null
                }
            </React.Fragment>
        )
    };

    private renderSettingsBtn = () => {
        return (
            <div className={styles.agBtnContainer}>
                <button onClick={this.toggleSettingsModal}
                        className={`${styles.agBtn} ${styles.settingsBtn}`}
                        title="Settings">
                </button>
            </div>
        )
    };

    // private renderScreenSharingBtn = () => {
    //     return (
    //         <React.Fragment>
    //             {this.props.attendeeMode !== 'audience' ?
    //                 (<div className={styles.agBtnContainer}>
    //                     <button onClick={this.screenShareInit}
    //                             className={`${styles.agBtn} ${styles.audioControlBtn}`}
    //                             title="Enable/Disable Screen Sharing">
    //                     </button>
    //                 </div>) :
    //                 null
    //             }
    //         </React.Fragment>
    //     )
    // };

    private renderExitBtn = () => {
        return (
            <div className={`${styles.agBtnContainer} ${this.state.readyState ? '' : styles.disabled}`}>
                <button onClick={this.handleExit}
                        className={`${styles.agBtn} ${styles.exitBtn}`}
                        title="Exit">
                    {/*<i className="ag-icon ag-icon-leave"/>*/}
                </button>
            </div>
        );
    };

    private changeConsultationStatus = (consultationDuration: number) => {
        if (this.props.userSecret && this.props.consultation && this.props.consultation.id) {
            return setConsultationDurationAPI(this.props.userSecret, this.props.consultation.id, consultationDuration).pipe(
                tap((resp: any) => {
                    this.props.changeOnlineConsultationStatus(resp.status);
                }),
                catchError((error: any) => {
                    return of(error);
                })
            ).subscribe();
        }
    };

    private setParticipantPresence = (isAvailable: boolean) => {
        if (!this.props.userSecret) {
            return;
        }

        this.subscriptions.push(
            confirmPresenceAPI(this.props.userSecret, isAvailable).pipe(
                catchError((error: any) => {
                    return of(error);
                })
            ).subscribe()
        )
    };

    private getDimensions = () => {
        this.setState({windowWidth: window.innerWidth});
    };

    private changeFullScreenMode = () => {
        this.setState({isFullScreenMode: !this.state.isFullScreenMode}, () => {
            this.props.changeScreenMode(this.state.isFullScreenMode)
        });
    };

    private toggleSettingsModal = () => {
        this.setState({isSettingsModalShown: !this.state.isSettingsModalShown});
    }
}

export default connect(
    (state: RootState) => ({
        audioInputDevice: audioInputDeviceSelector(state),
        audioOutputDevice: audioOutputDeviceSelector(state),
        videoInputDevice: videoInputDeviceSelector(state)
    }),
    {
        changeScreenMode,
        changeOnlineConsultationStatus
    }
)(AgoraVideoCall);
